import { useCallback } from "react";
import { useUndeliveredCardUrns } from "../../hooks/useUndeliveredCardUrns";
import { useGridCardApolloThrottle } from "./useGridCardApolloThrottle";

/**
 * The container's `isPebbleAvailable`: false drops a migrated GridCard pebble whose card the BFF did not
 * deliver (`undeliveredCardUrns`, recorded when useSelectedCardFetch asks for it), and the group removes
 * itself once every pebble is gone. Only the selection is fetched, so pebbles drop one at a time and a
 * dropped one is never re-requested.
 *
 * Legacy cards, the GridCard included while GRID_CARD_APOLLO is off, keep their connector's placeholder.
 * MarketCard is left alone: it renders nothing for an empty card, and its migrated gate is per urn.
 *
 * Not the same rule as `useMigratedPebbleGate`, which says which typenames render migrated: this one says
 * which of them drop an undelivered pebble, and so far that is GridCard only.
 */
export const useIsPebbleAvailable = (): ((urn: string, typename: string) => boolean) => {
  const undeliveredCardUrns = useUndeliveredCardUrns();
  const isGridCardApolloActive = useGridCardApolloThrottle();

  // Stable across renders: on web it reaches the memoized container, and web has no React Compiler.
  return useCallback(
    (urn: string, typename: string): boolean => {
      if (typename !== "GridCard" || !isGridCardApolloActive) {
        return true;
      }

      return !undeliveredCardUrns.includes(urn);
    },
    [undeliveredCardUrns, isGridCardApolloActive],
  );
};
